import React, { Fragment, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
    Drawer,
    IconButton,
    Typography,
    Box,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Hidden
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import MenuIcon from '@material-ui/icons/Menu';
import BusinessIcon from '@material-ui/icons/Business';
import AssignmentIcon from '@material-ui/icons/Assignment';
import PersonIcon from '@material-ui/icons/Person';
import DescriptionIcon from '@material-ui/icons/Description';
import SettingsIcon from '@material-ui/icons/Settings';
import NotificationsIcon from '@material-ui/icons/Notifications';
import HelpIcon from '@material-ui/icons/Help';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import { signOut } from '../../actions/authActions';
import { getNotifications } from '../../actions/userActions';
import Image from '../common/Image';
import noImageUrl from '../../img/no_image_small.jpg';
import history from '../../history';
import Alert from '../../utils/alert';

const useStyles = makeStyles((theme) => ({
    authHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: theme.spacing(1, 2),
    },
    userInfo: {
        display: 'flex',
        alignItems: 'center',
        marginRight: theme.spacing(2),
    },
    userImage: {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        overflow: 'hidden',
        '& img': {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
        },
    },
    userName: {
        fontSize: '1.4rem',
        color: '#4a4a4a',
        marginLeft: theme.spacing(2),
    },
    menuIconContainer: {
        textAlign: 'center',
        position: 'relative',
        cursor: 'pointer',
        marginTop: 0,
        marginBottom: 0,
        width: 'auto',
        padding: theme.spacing(1),
    },
    menuText: {
        fontSize: '1.4rem',
        fontWeight: 'bold',
        color: '#3273dc',
        marginTop: theme.spacing(0.5),
        lineHeight: 1,
    },
    notificationBadge: {
        position: 'absolute',
        top: '-1px',
        right: '-1px',
        backgroundColor: '#ff3860',
        color: 'white',
        borderRadius: '50%',
        padding: '0.25rem 0.5rem',
        fontSize: '1rem',
        minWidth: '20px',
        textAlign: 'center',
    },
    drawer: {
        '& .MuiDrawer-paper': {
            width: '30rem',
            height: 'auto',
            maxHeight: 'calc(100% - 8rem)',
            border: '2px solid #3273dc',
            position: 'fixed',
            top: '8rem',
            right: 0,
            transform: 'none',
        },
    },
    menuHeader: {
        backgroundColor: '#3273dc',
        color: 'white',
        padding: theme.spacing(2),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        '& h6': {
            fontSize: '2.4rem',
            fontWeight: 500,
        },
    },
    closeIcon: {
        color: 'white',
        '& svg': {
            fontSize: '2rem',
        },
    },
    menuList: {
        padding: 0,
        '& .MuiListItem-root': {
            borderBottom: '1px solid #dbdbdb',
            padding: theme.spacing(1.5, 2),
            '&:hover': {
                backgroundColor: '#f5f5f5',
            },
        },
        '& .MuiListItemIcon-root': {
            minWidth: '36px',
            color: '#3273dc',
        },
        '& .MuiListItemText-primary': {
            fontSize: '1.4rem',
            color: '#4a4a4a',
        },
    },
    menuItem: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        textDecoration: 'none',
        color: 'inherit',
    },
    notificationBadgeMenu: {
        backgroundColor: '#ff3860',
        color: 'white',
        width: '2rem',
        height: '2rem',
        borderRadius: '50%',
        fontSize: '1rem',
        marginLeft: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
    },
    menuIcon: {
        fontSize: '2.8rem',
        color: '#3273dc',
        marginTop: '4px',
        cursor: 'pointer',
        '&:hover': {
            opacity: 0.7,
        },
    },
}));

let notificationTimer;

function AuthHeader() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [menuActive, setMenuActive] = useState(false);
    const currentUser = useSelector(state => state.user.currentUser);
    const notifications = useSelector(state => state.user.notifications);

    useEffect(() => {
        notificationTimer = setInterval(() => {
            if (currentUser) {
                dispatch(getNotifications());
            }
        }, 1000 * 60 * 5); //5min

        return () => {
            clearInterval(notificationTimer);
        };
    }, [currentUser, dispatch]);

    const handleLogout = () => {
        dispatch(signOut());
        setMenuActive(false);
        history.push('/');
        Alert.success("ログアウトしました");
    };

    const getNotificationNum = () => {
        if (!currentUser || !notifications || notifications.length === 0)
            return 0;
        return notifications.filter(n => !n.checked).length;
    };

    const handleMenuItemClick = () => {
        setMenuActive(false);
    };

    const renderBusinessMenu = () => {
        return (
            <Fragment>
                <ListItem button component={Link} to="/mypage/profile" onClick={handleMenuItemClick}>
                    <ListItemIcon>
                        <BusinessIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="企業紹介編集" />
                </ListItem>
                <ListItem button component={Link} to="/mypage/postedjoblist" onClick={handleMenuItemClick}>
                    <ListItemIcon>
                        <AssignmentIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="求人投稿・編集" />
                </ListItem>
            </Fragment>
        );
    };

    const renderPersonalMenu = () => {
        return (
            <Fragment>
                <ListItem button component={Link} to="/mypage/profile" onClick={handleMenuItemClick}>
                    <ListItemIcon>
                        <PersonIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="プロフィール編集" />
                </ListItem>
                <ListItem button component={Link} to="/mypage/savedjoblist" onClick={handleMenuItemClick}>
                    <ListItemIcon>
                        <DescriptionIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="保存リスト" />
                </ListItem>
            </Fragment>
        );
    };

    const renderMenu = (user, notificationNum) => {
        return (
            <List className={classes.menuList}>
                {user.is_admin && (
                    <ListItem button component={Link} to="/admin" onClick={handleMenuItemClick}>
                        <ListItemIcon>
                            <SettingsIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Admin" />
                    </ListItem>
                )}
                {user.profile.user_type === 'Business' ? renderBusinessMenu() : renderPersonalMenu()}
                <ListItem button component={Link} to="/notifications" onClick={handleMenuItemClick}>
                    <ListItemIcon>
                        <NotificationsIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                        primary={
                            <Box display="flex" alignItems="center">
                                お知らせ
                                {notificationNum > 0 && (
                                    <span className={classes.notificationBadgeMenu}>
                                        {notificationNum}
                                    </span>
                                )}
                            </Box>
                        }
                    />
                </ListItem>
                <ListItem button component={Link} to="/mypage/account" onClick={handleMenuItemClick}>
                    <ListItemIcon>
                        <SettingsIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="アカウント設定" />
                </ListItem>
                <ListItem button component={Link} to="/help" onClick={handleMenuItemClick}>
                    <ListItemIcon>
                        <HelpIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="ヘルプ" />
                </ListItem>
                <ListItem button onClick={handleLogout}>
                    <ListItemIcon>
                        <ExitToAppIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="ログアウト" />
                </ListItem>
            </List>
        );
    };

    const renderGuestMenu = () => {
        return (
            <List className={classes.menuList}>
                <ListItem button component={Link} to="/signin" onClick={handleMenuItemClick}>
                    <ListItemIcon>
                        <ExitToAppIcon color="primary" style={{ transform: 'rotate(180deg)' }} />
                    </ListItemIcon>
                    <ListItemText primary="ログイン" />
                </ListItem>
                <ListItem button component={Link} to="/signup" onClick={handleMenuItemClick}>
                    <ListItemIcon>
                        <PersonAddIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="新規登録" />
                </ListItem>
                <ListItem button component={Link} to="/help" onClick={handleMenuItemClick}>
                    <ListItemIcon>
                        <HelpIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="ヘルプ" />
                </ListItem>
            </List>
        );
    };

    const renderLinks = () => {
        let notificationNum = getNotificationNum();

        return (
            <Fragment>
                <Box className={classes.menuIconContainer} onClick={() => setMenuActive(true)}>
                    <MenuIcon className={classes.menuIcon} />
                    <Hidden smDown>
                        <Typography className={classes.menuText} component="p" style={{ marginBottom: '4px' }}>メニュー</Typography>
                    </Hidden>
                    {notificationNum > 0 && (
                        <Box className={classes.notificationBadge}>
                            {notificationNum}
                        </Box>
                    )}
                </Box>
                <Drawer
                    anchor="right"
                    open={menuActive}
                    onClose={() => setMenuActive(false)}
                    className={classes.drawer}
                >
                    <Box className={classes.menuHeader}>
                        <Typography variant="h6">メニュー</Typography>
                        <IconButton
                            className={classes.closeIcon}
                            onClick={() => setMenuActive(false)}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    {currentUser ? renderMenu(currentUser, notificationNum) : renderGuestMenu()}
                </Drawer>
            </Fragment>
        );
    };

    const renderUserInfo = () => {
        return (
            <Box className={classes.userInfo}>
                {currentUser.profile?.avatar?.image_id ? (
                    <Box className={classes.userImage}>
                        <Image src={currentUser.profile?.avatar.image_url} size={'200'} alt={currentUser.name} />
                    </Box>
                ) : (
                    <Box className={classes.userImage}>
                        <Image src={noImageUrl} size={'200'} alt={currentUser.name} />
                    </Box>
                )}
                <Hidden xsDown>
                    <Typography className={classes.userName}>{currentUser.name}</Typography>
                </Hidden>
            </Box>
        );
    };

    return (
        <Box className={classes.authHeader}>
            {currentUser && renderUserInfo()}
            {renderLinks()}
        </Box>
    );
}

export default AuthHeader;
